import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
  BREAKPOINTS_EM,
  Button as ButtonBase,
  COLORS,
  cssByBreakpoint,
  SPACINGS,
  Tag,
  TYPOGRAPHY,
} from '@figure/figure-frontlib-gui';
import { numericFormatter } from '@figure/frontlib-util';
import { prettyPrintTerms } from 'shared/utils';
import { useRedirect } from 'shared/hooks';

const Heading = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.S}px;
  flex-wrap: wrap;

  > h5 {
    ${TYPOGRAPHY.H5};
  }
`;

const Copy = styled.p`
  ${TYPOGRAPHY.S_SEMIBOLD};
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${SPACINGS.X2L}px;
`;

const Details = styled.div`
  display: flex;
  gap: ${SPACINGS.M}px;

  > * {
    width: 50%;
  }

  :not(:first-of-type) {
    padding-top: ${SPACINGS.S}px;
  }

  :not(:last-of-type) {
    padding-bottom: ${SPACINGS.S}px;
    border-bottom: 1px solid ${COLORS.NEUTRAL_200};
  }
`;

const Button = styled(ButtonBase)`
  ${cssByBreakpoint(
    [
      css`
        min-width: initial;
        width: 100%;
      `,
      css`
        min-width: 340px;
        width: initial;
      `,
    ],
    BREAKPOINTS_EM.MD
  )}
`;
const Container = styled.div`
  padding: 24px;
  background-color: ${COLORS.NEUTRAL_100};
  border-radius: ${SPACINGS.S}px;

  > ${Heading} {
    margin-bottom: ${SPACINGS.L}px;
  }

  > ${Copy} {
    margin-bottom: ${SPACINGS.X2L}px;
  }
`;

const HelocOfferCard = ({ helocOfferPackage }) => {
  const { redirectToLocation } = useRedirect();
  const handleHelocClick = () =>
    redirectToLocation(
      `${process.env.REACT_APP_HELOC_PATH}/${helocOfferPackage.applicationUuid}/${
        helocOfferPackage.lienValidationRequired ? 'liens' : 'product/select'
      }`,
      true
    );

  const copyText = helocOfferPackage.lienValidationRequired
    ? 'You may qualify for a HELOC, which offers lower, fixed rates and more cash to borrow. Click “Check eligibility” to confirm details about your property and see if you qualify for any HELOC offers.'
    : 'You’re prequalified for a HELOC, which offers lower, fixed rates and more cash to borrow! Click “Explore offers” to view your HELOC options.';
  const maxLoanAmount = helocOfferPackage.lienValidationRequired ? 250000 : helocOfferPackage?.max;
  const terms = helocOfferPackage.lienValidationRequired
    ? [5, 10, 15, 30]
    : helocOfferPackage?.termsList;
  const buttonText = helocOfferPackage.lienValidationRequired
    ? 'Check eligibility'
    : 'Explore offers';

  return (
    <Container>
      <Heading>
        <h5>Home Equity Line</h5>
        <Tag>Alternative option</Tag>
      </Heading>

      <Copy>{copyText}</Copy>

      <DetailsContainer>
        <Details>
          <p>Max Loan Amount</p>
          <p>{numericFormatter.formatMoney(maxLoanAmount)}</p>
        </Details>
        <Details>
          <p>Funding Time</p>
          <p>5 days</p>
        </Details>
        <Details>
          <p>Fixed Term Options</p>
          <p>{prettyPrintTerms(terms)}</p>
        </Details>
      </DetailsContainer>

      <Button
        onClick={handleHelocClick}
        variant={helocOfferPackage.lienValidationRequired ? 'secondary' : 'primary'}
      >
        {buttonText}
      </Button>
    </Container>
  );
};

HelocOfferCard.propTypes = {
  helocOfferPackage: PropTypes.shape({
    max: PropTypes.number,
    termsList: PropTypes.arrayOf(PropTypes.number),
    applicationUuid: PropTypes.string,
    lienValidationRequired: PropTypes.bool,
  }).isRequired,
};

export default HelocOfferCard;
